/**
 * WaitingListStats - KPIs no topo da página (mockup v2).
 *
 * KPIs focados no dia a dia do gestor/recepcionista:
 * 1. Pacientes aguardando  — quantos estão na fila agora
 * 2. Com encaixe possível — quantas pessoas têm horário livre compatível
 * 3. Contatados            — quantos estão aguardando retorno
 * 4. Convertidos este mês  — quantos foram agendados via lista no mês atual
 */
import React from 'react';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import styles from '../WaitingListV2Page.module.css';

// slots: os mesmos horários livres da faixa "N horários livres" e da aba Horários
// (já filtrados pelo período); periodLabel: "de 04/11 a 11/11"
function WaitingListStats({ patients = [], slots = [], periodLabel = '' }) {
  // 1. Pessoas aguardando: ainda na fila, sem agendamento (PENDING é o status de
  // quem acabou de entrar; WAITING fica pelos registros antigos)
  const WAITING_STATUSES = ['PENDING', 'WAITING', 'CONTACTED', 'CONFIRMED'];
  const waiting = patients.filter((p) => !p.status || WAITING_STATUSES.includes(p.status));
  const highPriority = waiting.filter((p) => p.priority === 'HIGH').length;

  // 2. Pessoas com pelo menos 1 horário livre que combina com elas, e quantos
  // horários livres existem para a lista toda no período (mesma conta da faixa
  // da lista e da aba Horários; o mesmo horário serve a várias pessoas)
  const patientsWithOpportunity = new Set();
  const totalCompatibleSlots = slots.length;
  slots.forEach((slot) => {
    (slot.patients || []).forEach((p) => {
      if (p.waitingListItem?.id) patientsWithOpportunity.add(p.waitingListItem.id);
    });
  });

  // 3. Contatados aguardando retorno
  const contacted = patients.filter((p) => p.status === 'CONTACTED').length;

  // 4. Convertidos no mês atual
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const scheduledThisMonth = patients.filter((p) => {
    if (p.status !== 'SCHEDULED') return false;
    if (!p.updatedAt && !p.createdAt) return true; // sem data, conta mesmo assim
    const ref = new Date(p.updatedAt || p.createdAt);
    return ref >= startOfMonth;
  }).length;

  const monthName = now.toLocaleDateString('pt-BR', { month: 'long' });

  const stats = [
    {
      icon: <PeopleOutlineIcon style={{ fontSize: '1.3rem', color: '#7c3aed' }} />,
      iconBg: '#f5f3ff',
      value: waiting.length,
      label: 'Aguardando',
      sublabel: highPriority > 0
        ? `${highPriority} alta prioridade`
        : 'na lista de espera',
    },
    {
      icon: <EventAvailableIcon style={{ fontSize: '1.3rem', color: '#059669' }} />,
      iconBg: '#ecfdf5',
      value: patientsWithOpportunity.size,
      label: 'Com encaixe possível',
      sublabel: totalCompatibleSlots > 0
        ? `${totalCompatibleSlots} horário${totalCompatibleSlots !== 1 ? 's' : ''} livre${totalCompatibleSlots !== 1 ? 's' : ''}${periodLabel ? ` ${periodLabel}` : ''}`
        : `nenhum horário livre${periodLabel ? ` ${periodLabel}` : ''}`,
    },
    {
      icon: <MarkEmailReadIcon style={{ fontSize: '1.3rem', color: '#2563eb' }} />,
      iconBg: '#eff6ff',
      value: contacted,
      label: 'Contatados',
      sublabel: 'aguardando retorno',
    },
    {
      icon: <CheckCircleOutlineIcon style={{ fontSize: '1.3rem', color: '#d97706' }} />,
      iconBg: '#fffbeb',
      value: scheduledThisMonth,
      label: 'Convertidos',
      sublabel: `em ${monthName}`,
    },
  ];

  return (
    <div className={styles.statsContainer}>
      {stats.map((stat) => (
        <div key={stat.label} className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: stat.iconBg }}>
            {stat.icon}
          </div>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{stat.value}</span>
            <span className={styles.statLabel}>{stat.label}</span>
            <span className={styles.statSublabel}>{stat.sublabel}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default WaitingListStats;
